import _ from 'underscore'
import React from 'react'
import PropTypes from 'prop-types'

import Form from '../elements/Form'
import TextInput from '../elements/forms/TextInput'
import StaticInput from '../elements/forms/StaticInput'
import GenericSelect from '../elements/forms/GenericSelect'
import Dismissible from '../elements/Dismissible'
import AppConstants from '../../utils/AppConstants'
import AppUtils from '../../utils/AppUtils'

const ERROR = {
  EMPTY_PRODUCT: 'Veuillez sélectionner un matériel',
  EMPTY_OPERATION: 'L\'opération ne peut pas être vide.',
  EMPTY_CONTACT: 'Veuillez sélectionner un contact d\'entretien'
}

class ControlledProductForm extends React.Component {

  constructor(props) {

    // Properties
    super(props)

    // State
    this.state = {
      input: this.getInitialInput(props),
      formErrors: []
    }

    // Bind events
    this.onChangeInputProduct = this.onChangeInputProduct.bind(this)
    this.onChangeInputOperation = this.onChangeInputOperation.bind(this)
    this.onChangeInputContactType = this.onChangeInputContactType.bind(this)
    this.onChangeInputContactName = this.onChangeInputContactName.bind(this)
    this.onSubmit = this.onSubmit.bind(this)

    // Register submit event
    if (_.isFunction(this.props.registerSubmit)) {
      this.props.registerSubmit(this.onSubmit)
    }
  }

  getInitialInput(props = this.props) {
    return _.extend(
      {
        operation: (props.data && props.data.operation) || '',
        type: (props.data && props.data.contact.type) || AppConstants.CONTACT.TYPE.SELF,
        contact: (props.data && props.data.contact.user && props.data.contact.user.id) || null
      },
      props.data
        ? {}
        : {
            product_id: null
          }
    )
  }

  setInput(input = {}) {
    this.setState((prevState, props) => ({
      input: _.extend(
        {},
        prevState.input,
        input
      )
    }))
  }

  componentDidUpdate(prevProps, prevState) {
    // When async fetch finished
    if (prevProps.isLoading && !this.props.isLoading) {
      this.setState({
        input: this.getInitialInput()
      })
    }
  }

  onChangeInputProduct(product_id) {
    this.setInput({ product_id })
  }

  onChangeInputOperation(operation) {
    this.setInput({ operation })
  }

  onChangeInputContactType(type) {
    this.setInput(_.extend(
      { type },
      type === AppConstants.CONTACT.TYPE.SPECIFIC
        ? {}
        : {
            contact: null
          }
    ))
  }

  onChangeInputContactName(contact) {
    this.setInput({ contact })
  }

  onSubmit() {
    // Check that fields have been filled
    const formErrors = []
    if (_.isNull(this.state.input.product_id)) {
      formErrors.push(ERROR.EMPTY_PRODUCT)
    }
    if (!_.isString(this.state.input.operation) || this.state.input.operation.length === 0) {
      formErrors.push(ERROR.EMPTY_OPERATION)
    }
    if (this.state.input.type === AppConstants.CONTACT.TYPE.SPECIFIC && _.isNull(this.state.input.contact)) {
      formErrors.push(ERROR.EMPTY_CONTACT)
    }
    this.setState({
      formErrors
    })
    if (!_.isEmpty(formErrors)) {
      return;
    }

    // Build and submit correctly formatted input
    const input = _.extend(
      {},
      _.pick(this.state.input, 'operation', 'product_id'),
      {
        contact: _.extend(
          _.pick(this.state.input, 'type'),
          this.state.input.type === AppConstants.CONTACT.TYPE.SPECIFIC
            ? {
                user: {
                  id: this.state.input.contact
                }
              }
            : {}
        )
      }
    )
    this.props.onSubmit(input)
  }

  render() {
    return (
      <React.Fragment>
        {
          this.props.error
            ? <Dismissible
                header={AppUtils.errorToTitle(this.props.error)}
                content={AppUtils.errorToText(this.props.error, {
                  [AppConstants.ERROR.BAD_REQUEST.UNICITY_CONSTRAINT_VIOLATION]: 'Le matériel sélectionné fait déjà partie de ce contrôle.'
                })}
                color='red'
                onDismiss={this.props.onClearError}
              />
            : null
        }
        {
          !_.isEmpty(this.state.formErrors)
            ? <Dismissible
                content='Veuillez résoudre les problèmes ci-dessous avant de poursuivre.'
                color='orange'
              />
            : null
        }
        <Form
          onSubmit={this.onSubmit}
          loading={this.props.isLoading || this.props.isSaving}
        >
          {
            this.props.data
              ? <StaticInput
                  label='Matériel'
                  content={this.props.data.product.name}
                />
              : <GenericSelect
                  label='Matériel'
                  loading={this.props.productsIsFetching}
                  options={
                    _.isArray(this.props.productsList)
                      ? _.map(
                          _.flatten(
                            _.map(
                              this.props.productsList,
                              (category) => (category.products)
                            )
                          ),
                          (product) => ({
                            key: product.id,
                            value: product.name
                          })
                        )
                      : []
                  }
                  selected={this.state.input.product_id}
                  onChange={this.onChangeInputProduct}
                  error={
                    _.contains(this.state.formErrors, ERROR.EMPTY_PRODUCT)
                      ? ERROR.EMPTY_PRODUCT
                      : null
                  }
                  canSearch={true}
                />
          }
          <TextInput
            label='Opération'
            placeholder='Changer les piles'
            value={this.state.input.operation}
            onChange={this.onChangeInputOperation}
            error={
              _.contains(this.state.formErrors, ERROR.EMPTY_OPERATION)
                ? ERROR.EMPTY_OPERATION
                : null
            }
            multiline={true}
          />
          <GenericSelect
            label="Contact d'entretien"
            options={[
              {
                key: AppConstants.CONTACT.TYPE.SELF,
                value: 'Celui qui effectue le contrôle'
              },
              {
                key: AppConstants.CONTACT.TYPE.OWNER,
                value: 'Responsable du groupe matériel'
              },
              {
                key: AppConstants.CONTACT.TYPE.SPECIFIC,
                value: 'Une personne spécifique'
              }
            ]}
            selected={this.state.input.type}
            onChange={this.onChangeInputContactType}
          />
          {
            this.state.input.type === AppConstants.CONTACT.TYPE.SPECIFIC
              ? <GenericSelect
                  label=''
                  loading={this.props.usersIsFetching}
                  options={
                    _.isArray(this.props.usersList)
                      ? _.map(
                          this.props.usersList,
                          (user) => ({
                            key: user.id,
                            value: user.name
                          })
                        )
                      : []
                  }
                  selected={this.state.input.contact}
                  onChange={this.onChangeInputContactName}
                  error={
                    _.contains(this.state.formErrors, ERROR.EMPTY_CONTACT)
                      ? ERROR.EMPTY_CONTACT
                      : null
                  }
                  canSearch={true}
                />
              : null
          }
        </Form>
      </React.Fragment>
    )
  }
}

ControlledProductForm.propTypes = {
  data: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onClearError: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool.isRequired,
  registerSubmit: PropTypes.func,
  error: PropTypes.object,
  productsList: PropTypes.array,
  productsIsFetching: PropTypes.bool.isRequired,
  usersList: PropTypes.array,
  usersIsFetching: PropTypes.bool.isRequired
}

export default ControlledProductForm



// WEBPACK FOOTER //
// ./src/components/forms/ControlledProductForm.js